import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';

export interface INotification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  /** Optional single action button rendered on the toast (e.g. "Mute chime").
   *  Transient — not a persistent UI control, so it needs no coach-mark. */
  action?: { label: string; onClick: () => void };
}

interface NotificationContextType {
  notifications: INotification[];
  addNotification: (notification: Omit<INotification, 'id'>) => void;
  removeNotification: (id: string) => void;
  showNotification: (
    message: string,
    type?: 'success' | 'error' | 'warning' | 'info',
    action?: { label: string; onClick: () => void },
  ) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

export const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  addNotification: () => {},
  removeNotification: () => {},
  showNotification: () => {},
  success: () => {},
  error: () => {},
  warning: () => {},
  info: () => {},
});

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

let notificationCounter = 0;

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<INotification[]>([]);

  const addNotification = useCallback((notification: Omit<INotification, 'id'>) => {
    const id = `${Date.now()}-${++notificationCounter}`;
    const newNotification = { ...notification, id };
    
    setNotifications(prev => [...prev, newNotification]);

    // Auto remove after duration
    const duration = notification.duration || 5000;
    setTimeout(() => {
      removeNotification(id);
    }, duration);
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const success = useCallback((title: string, message?: string) => {
    addNotification({ type: 'success', title, message });
  }, [addNotification]);

  const error = useCallback((title: string, message?: string) => {
    addNotification({ type: 'error', title, message, duration: 7000 });
  }, [addNotification]);

  const warning = useCallback((title: string, message?: string) => {
    addNotification({ type: 'warning', title, message });
  }, [addNotification]);

  const info = useCallback((title: string, message?: string) => {
    addNotification({ type: 'info', title, message });
  }, [addNotification]);

  const showNotification = useCallback((
    message: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    action?: { label: string; onClick: () => void },
  ) => {
    addNotification({ type, title: message, action });
  }, [addNotification]);

  const value = React.useMemo(() => ({
    notifications,
    addNotification,
    removeNotification,
    showNotification,
    success,
    error,
    warning,
    info,
  }), [notifications, addNotification, removeNotification, showNotification, success, error, warning, info]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer
        notifications={notifications}
        onDismiss={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

const toastStyles: Record<INotification['type'], string> = {
  success: 'bg-green-600 border-green-500',
  error: 'bg-red-600 border-red-500',
  warning: 'bg-amber-600 border-amber-500',
  info: 'bg-slate-700 border-slate-600',
};

const NotificationContainer: React.FC<{
  notifications: INotification[];
  onDismiss: (id: string) => void;
}> = ({ notifications, onDismiss }) => {
  if (notifications.length === 0) return null;
  return (
    <div
      aria-live="polite"
      className="pointer-events-none fixed bottom-4 right-4 z-[9999] flex w-full max-w-sm flex-col gap-2"
    >
      {notifications.map((n) => (
        <div
          key={n.id}
          role="alert"
          className={`pointer-events-auto flex items-start gap-3 rounded-md border px-4 py-3 text-sm text-white shadow-lg ${toastStyles[n.type]}`}
        >
          <div className="flex-1">
            <div className="font-medium">{n.title}</div>
            {n.message && <div className="mt-0.5 text-xs opacity-90">{n.message}</div>}
            {n.action && (
              <button
                type="button"
                onClick={() => { n.action!.onClick(); onDismiss(n.id); }}
                className="mt-1 text-xs font-medium text-white underline underline-offset-2 opacity-90 hover:opacity-100"
              >
                {n.action.label}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => onDismiss(n.id)}
            className="text-white/80 hover:text-white"
            aria-label="Dismiss notification"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
};
